import { AlertCircle, Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/useAuth"
import {
  DownloadExpiredError,
  getFreshDownloadUrl,
  s3KeyFromGeneratedUrl,
} from "@/services/uploadService"
import { GeneratedImage } from "./GeneratedImage"

interface PollingImageProps {
  /** Presigned URL of generated/{request_id}/status.json (the async image job's state object). */
  statusUrl: string
}

type JobState =
  | { phase: "polling" }
  | { phase: "ready"; url: string }
  | { phase: "failed"; message: string }

const POLL_INTERVAL_MS = 3000
// Keep the UI poll window at/above the backend's own ceilings so we never show "failed" while
// the job could still succeed (and bill). The image-gen Lambda's worst case is maxEventAge=5min
// queued + generation budget (_BUDGET_S=780s ≈ 13min) = 18min; the backend always writes a
// terminal status.json within that, so 18min is the point past which it cannot still succeed.
// The poll stops the instant status.json is terminal, so the common ~3min case is unaffected.
const MAX_DURATION_MS = 18 * 60 * 1000

const ERROR_MESSAGES: Record<string, string> = {
  rate_limited: "アクセスが混み合っています。少し待ってからもう一度お試しください。",
  provider_timeout: "生成に時間がかかりすぎました。もう一度お試しください。",
  invalid_request: "リクエストの内容に問題がありました。言い方を変えてお試しください。",
  auth: "画像生成サービスに接続できませんでした（管理者にお知らせください）。",
  internal: "画像の生成に失敗しました。もう一度お試しください。",
}

function failMessage(errorType?: string): string {
  return (errorType && ERROR_MESSAGES[errorType]) || ERROR_MESSAGES.internal
}

/**
 * Renders an asynchronously-generated image by polling its status.json job object.
 *
 * The backend fires image generation as a fire-and-forget Lambda and returns a presigned
 * status.json URL immediately (so the chat's SSE connection closes in seconds instead of
 * being held — and dropped — through the multi-minute generation). This component polls that
 * status object until the job reaches a terminal state:
 *   - 404            → job hasn't written status yet (just started) → keep polling
 *   - {running}      → keep polling
 *   - {succeeded,url}→ show the image
 *   - {failed,...}   → show a friendly error
 *   - 403/expired    → terminal error
 */
export function PollingImage({ statusUrl }: PollingImageProps) {
  const [state, setState] = useState<JobState>({ phase: "polling" })
  const startedAt = useRef<number>(Date.now())
  const { token } = useAuth()
  // poll クロージャから常に最新のトークンを読むための ref（effect は statusUrl だけに反応）。
  const tokenRef = useRef<string | undefined>(token)
  tokenRef.current = token

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // 履歴に保存された status URL の署名は約1時間で失効する（2026-07-07 実走FB:
    // 古いチャットを開くと 403 で「失敗」表示になっていた）。403 を踏んだら一度だけ
    // 認証付き API で新しい署名付き URL に取り替えて続行する。
    let effectiveUrl = statusUrl
    let refreshed = false
    startedAt.current = Date.now()
    // A new/updated status URL (e.g. a complete URL replacing a mid-stream prefix) restarts the
    // job: reset to polling so a stale "failed"/"ready" from the previous URL isn't shown.
    setState({ phase: "polling" })

    const poll = async () => {
      if (cancelled) return
      if (Date.now() - startedAt.current > MAX_DURATION_MS) {
        setState({ phase: "failed", message: "生成に時間がかかっています。もう一度試してみてください。" })
        return
      }
      try {
        const res = await fetch(effectiveUrl, { cache: "no-store" })
        if (cancelled) return
        if (res.status === 404) {
          // status.json not written yet — generation just started.
          timer = setTimeout(poll, POLL_INTERVAL_MS)
          return
        }
        if (res.status === 403) {
          const fileKey = s3KeyFromGeneratedUrl(statusUrl)
          if (!refreshed && fileKey && tokenRef.current) {
            refreshed = true
            try {
              effectiveUrl = await getFreshDownloadUrl(fileKey, tokenRef.current)
              if (cancelled) return
              timer = setTimeout(poll, 0)
              return
            } catch (err) {
              if (cancelled) return
              if (err instanceof DownloadExpiredError) {
                setState({
                  phase: "failed",
                  message: "画像の保存期間（7日）を過ぎています。必要ならもう一度作成を頼んでください。",
                })
                return
              }
            }
          }
          setState({ phase: "failed", message: "画像リンクの有効期限が切れました。もう一度お試しください。" })
          return
        }
        if (!res.ok) {
          // Transient (5xx etc.) — keep trying within the time budget.
          timer = setTimeout(poll, POLL_INTERVAL_MS)
          return
        }
        const data = (await res.json()) as { state?: string; url?: string; error_type?: string }
        if (cancelled) return
        if (data.state === "succeeded" && data.url) {
          setState({ phase: "ready", url: data.url })
        } else if (data.state === "failed") {
          setState({ phase: "failed", message: failMessage(data.error_type) })
        } else {
          // running (or unknown) — keep polling.
          timer = setTimeout(poll, POLL_INTERVAL_MS)
        }
      } catch {
        if (cancelled) return
        // Network blip — keep trying within the budget.
        timer = setTimeout(poll, POLL_INTERVAL_MS)
      }
    }

    poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [statusUrl])

  if (state.phase === "polling") {
    return (
      <div className="flex items-center gap-2 my-2 p-3 bg-white border border-gray-200 rounded-lg shadow-sm text-gray-600">
        <Loader2 size={18} className="animate-spin text-purple-600" />
        <span className="text-sm">画像を生成中です…（数十秒〜2分ほどお待ちください）</span>
      </div>
    )
  }

  if (state.phase === "failed") {
    return (
      <div className="flex items-center gap-2 my-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle size={18} className="flex-shrink-0" />
        <span className="text-sm">{state.message}</span>
      </div>
    )
  }

  // 完成画像の表示は GeneratedImage に委譲する。status.json 内の画像 URL も生成時の
  // 署名（約1時間）なので、古いチャットでは表示エラー→署名リフレッシュが必要になる。
  return <GeneratedImage href={state.url} filename="生成された画像.png" />
}
